import { Box, Typography, useTheme, Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell, ReferenceLine, Text } from "recharts";
import DownloadComponentButton from "../../components/button/DownloadComponentButton";
import { useGetColor } from "./Colors";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const MAX_YAXIS_WIDTH = 280;

const CustomYAxisTick = (props) => {
  const { x, y, payload, font, color, width } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <Text
        x={-10}
        y={0}
        width={width - 20}
        textAnchor="end"
        verticalAnchor="middle"
        style={{
          fontFamily: font.family,
          fontSize: parseInt(font.sizes.standard),
          fill: color,
        }}
      >
        {payload.value}
      </Text>
    </g>
  );
};

const HorizontalStackedBarChart = ({
  title, data = [], dataKeys = [], getColor,
  showLegend = false, domain, height, barSize,
  coloredBars = false, xAxisLabel, yAxisLabel,
  grouped = false, showMean = false, customMeanValue = 0, meanLabel,
  scale = "auto",
  hideDownload = false,
  fillHeight = false
}) => {
  const { t } = useTranslation();
  const [yAxisWidth, setYAxisWidth] = useState(150);
  const [dynamicBarSize, setDynamicBarSize] = useState(18);
  const [visibility, setVisibility] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const getDefaultColor = useGetColor();

  const font = GRAPH_PALETTE.font;
  const axes = GRAPH_PALETTE.axes;

  useEffect(() => {
    if (data && data.length > 0) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.font = `${font.sizes.standard} ${font.family}`;
      const spaceWidth = ctx.measureText(" ").width;
      const fontSizePx = parseInt(font.sizes.standard) || 14;
      const lineHeight = fontSizePx * 1.2;

      let maxRawWidth = 0;
      let maxLines = 1;

      data.forEach((d) => {
        const label = d.label || "";
        const rawWidth = ctx.measureText(label).width + 35;
        if (rawWidth > maxRawWidth) maxRawWidth = rawWidth;

        // Simulate Recharts Text word-wrap (splits at spaces)
        const textAreaWidth = Math.min(rawWidth, MAX_YAXIS_WIDTH) - 20;
        const words = label.split(" ");
        let lines = 1;
        let lineWidth = 0;
        words.forEach((word) => {
          const wordWidth = ctx.measureText(word).width;
          if (lineWidth > 0 && lineWidth + spaceWidth + wordWidth > textAreaWidth) {
            lines++;
            lineWidth = wordWidth;
          } else {
            lineWidth += (lineWidth > 0 ? spaceWidth : 0) + wordWidth;
          }
        });
        if (lines > maxLines) maxLines = lines;
      });

      setYAxisWidth(Math.min(Math.ceil(maxRawWidth), MAX_YAXIS_WIDTH));
      setDynamicBarSize(Math.max(18, Math.ceil(maxLines * lineHeight) + 6));
    }
  }, [data, font.sizes.standard, font.family]);

  const chartData = scale === "log"
    ? data.map(d => {
      const entry = { ...d };
      dataKeys.forEach(k => {
        if (entry[k] <= 0) entry[k] = 0.0001;
      });
      return entry;
    })
    : data;

  const slicedData = showAll ? chartData : chartData.slice(0, 15);
  const innerHeight = `${slicedData.length * (barSize || dynamicBarSize) + 80}px`;

  return (
    <Box
      onMouseEnter={() => setVisibility(true)}
      onMouseLeave={() => setVisibility(false)}
      sx={{
        width: "100%",
        height: fillHeight ? "100%" : "auto",
        flexGrow: fillHeight ? 1 : undefined,
        minHeight: fillHeight ? 0 : undefined,
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      <Box
        ref={ref}
        sx={{
          flexGrow: fillHeight ? 1 : undefined,
          height: fillHeight ? undefined : "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: tok.bg.card
        }}
      >
        {title && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: tok.border.divider
          }}>
            <Typography variant="Chart Title" sx={{ color: tok.text.primary, fontWeight: 600 }}>
              {t(title)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {data.length > 15 && (
                <Button
                  size="small"
                  onClick={() => setShowAll(!showAll)}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    py: 0.5,
                    px: 1.2,
                    borderRadius: "6px",
                    backgroundColor: "#659d33",
                    color: "#ffffff",
                    border: "1px solid #659d33",
                    "&:hover": {
                      backgroundColor: "#55842b",
                      borderColor: "#55842b",
                      boxShadow: "0 2px 8px rgba(101, 157, 51, 0.3)"
                    }
                  }}
                >
                  {showAll ? t("Show Top 15") : t("Show All")}
                </Button>
              )}
              {!hideDownload && (
                <DownloadComponentButton ref={ref} filename={title + ".png"} visibility={visibility} />
              )}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            flexGrow: 1,
            position: "relative",
            px: 2,
            pb: 1,
            minHeight: 0,
            overflow: "visible",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)",
            }
          }}
        >
          <div style={{ height: fillHeight ? "100%" : innerHeight, minHeight: fillHeight ? innerHeight : undefined, width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={slicedData}
                layout="vertical"
                margin={{ top: 35, right: 20, left: yAxisLabel ? 80 : 20, bottom: xAxisLabel ? 55 : 15 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={false} opacity={0.2} />
                <XAxis
                  type="number"
                  domain={scale === "log" ? [0.0001, 'auto'] : (domain || [0, "auto"])}
                  scale={scale}
                  allowDataOverflow={true}
                  axisLine={{ stroke: isDark ? axes.darkMode.line : axes.lightMode.line, strokeWidth: axes.strokeWidth }}
                  tickLine={{ stroke: isDark ? axes.darkMode.tick : axes.lightMode.tick, strokeWidth: 1.5 }}
                  tick={{ fontFamily: font.family, fontSize: parseInt(font.sizes.standard), fill: theme.palette.text.primary }}
                  label={xAxisLabel ? { value: t(xAxisLabel), position: 'bottom', offset: 10, fill: theme.palette.text.primary, fontSize: parseInt(font.sizes.axisLabel), fontFamily: font.family } : undefined}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={yAxisWidth}
                  axisLine={{ stroke: isDark ? axes.darkMode.line : axes.lightMode.line, strokeWidth: axes.strokeWidth }}
                  tickLine={{ stroke: isDark ? axes.darkMode.tick : axes.lightMode.tick, strokeWidth: 1.5 }}
                  tick={<CustomYAxisTick font={font} color={theme.palette.text.primary} width={yAxisWidth} />}
                  label={yAxisLabel ? {
                    content: ({ viewBox }) => {
                      const cx = viewBox.x - 45;
                      const cy = viewBox.y + viewBox.height / 2;
                      return (
                        <text
                          x={cx}
                          y={cy}
                          transform={`rotate(-90, ${cx}, ${cy})`}
                          textAnchor="middle"
                          fill={theme.palette.text.primary}
                          fontSize={parseInt(font.sizes.axisLabel)}
                          fontWeight={font.weights.bold}
                          fontFamily={font.family}
                        >
                          {t(yAxisLabel)}
                        </text>
                      );
                    }
                  } : undefined}
                />
                <Tooltip
                  formatter={(value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  contentStyle={{ fontFamily: font.family, fontSize: font.sizes.standard, borderRadius: "8px" }}
                />
                {showMean && customMeanValue > 0 && (
                  <ReferenceLine
                    x={customMeanValue}
                    stroke="#659d33"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    label={{
                      value: `${meanLabel || t('Mean')}: ${Number(customMeanValue).toLocaleString(undefined, { maximumFractionDigits: 1 })}`,
                      position: 'top',
                      offset: 10,
                      fill: '#659d33',
                      fontSize: parseInt(font.sizes.standard),
                      fontFamily: font.family,
                      fontWeight: 600
                    }}
                  />
                )}
                {dataKeys.map((d, index) => (
                  <Bar
                    key={d}
                    dataKey={d}
                    {...(!grouped ? { stackId: "stack" } : {})}
                    fill={!coloredBars ? (getColor ? getColor(index, dataKeys.length) : getDefaultColor(index, dataKeys.length)) : getDefaultColor(index, dataKeys.length)}
                    barSize={barSize || dynamicBarSize}
                  >
                    {coloredBars && slicedData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={getColor ? getColor(idx, slicedData.length) : getDefaultColor(idx, slicedData.length)} />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Box>
        {showLegend && (
          <Box sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2.5,
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${tok.border.divider}`,
            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)"
          }}>
            {dataKeys.map((key, index) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: !coloredBars ? (getColor ? getColor(index, dataKeys.length) : getDefaultColor(index, dataKeys.length)) : getDefaultColor(index, dataKeys.length)
                }} />
                <Typography sx={{
                  fontFamily: font.family,
                  fontSize: 12,
                  fontWeight: 500,
                  color: theme.palette.text.secondary
                }}>
                  {t(key)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HorizontalStackedBarChart;
